// @catalog F.030
// ErosionBreakdown — barras escalonadas desde el margen a precio lista hasta el
// margen real. El descuento ya está embebido en la venta neta (Odoo
// price_subtotal_incl), por eso la base es el margen A PRECIO LISTA
// (= (venta_neta + descuentos) − costo) y el descuento se resta UNA sola vez
// (lista → neto). Restarlo sobre la venta neta sería doble conteo (BUG-005).
// El margen real (ingreso − costo, con IVA) reconcilia con bancos (D5); la
// comisión se expone aparte en su propio KPI, no reduce este margen. Aplica
// solo a /margenes.
import { Box } from "@mui/material";

interface ErosionBreakdownProps {
  // Venta NETA del periodo (price_subtotal_incl / price_total) — YA neto de
  // descuento. La venta a precio lista se deriva como ventaNeta + descuentos.
  ventaNeta: number;
  // Costo de venta absoluto (con IVA, costo real de última compra).
  costo: number;
  // Descuentos = brecha vs precio lista GREATEST(0, qty*list_price − price_subtotal)
  // (valor positivo; se resta una sola vez desde el margen a precio lista).
  descuentos: number;
  formatValue: (v: number) => string;
}

export function ErosionBreakdown(props: ErosionBreakdownProps): JSX.Element {
  const { ventaNeta, costo, descuentos, formatValue } = props;
  // Margen a precio lista = (venta a precio lista) − costo. El descuento aún NO
  // se ha aplicado en esta base (la venta a lista lo "devuelve" sumándolo).
  const ventaLista = ventaNeta + descuentos;
  const margenLista = Math.max(0, ventaLista - costo);
  const items = [
    {
      label: "Margen a precio lista",
      value: margenLista,
      color: "var(--accent)",
      sign: 1,
    },
    {
      label: "Descuentos aplicados",
      value: descuentos,
      color: "var(--warn)",
      sign: -1,
    },
  ] as const;
  // margenLista − descuentos = venta_neta − costo = margen real (reconcilia con
  // bancos). El descuento se resta exactamente una vez (NO doble conteo).
  const margenReal = margenLista - descuentos;
  // % sobre venta NETA para coincidir con el KPI de margen del header.
  const marginPctOfSales = ventaNeta > 0 ? (margenReal / ventaNeta) * 100 : 0;

  return (
    <Box>
      {items.map((it) => {
        const widthPct =
          margenLista > 0
            ? Math.min(100, (it.value / margenLista) * 100)
            : 0;
        return (
          <Box key={it.label} sx={{ marginBottom: "10px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "12px",
              }}
            >
              <Box component="span" sx={{ color: "var(--ink-2)" }}>
                {it.label}
              </Box>
              <Box
                component="span"
                sx={{
                  color: it.sign > 0 ? "var(--ink-1)" : "var(--neg)",
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {it.sign > 0 ? "" : "−"}
                {formatValue(it.value)}
              </Box>
            </Box>
            <Box
              sx={{
                height: "4px",
                backgroundColor: "var(--bg-2)",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${widthPct}%`,
                  backgroundColor: it.color,
                  borderRadius: "2px",
                }}
              />
            </Box>
          </Box>
        );
      })}
      <Box sx={{ height: "1px", backgroundColor: "var(--border)", margin: "16px 0" }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 0",
        }}
      >
        <Box component="span" sx={{ fontSize: "13px", fontWeight: 600 }}>
          Margen real
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "var(--ink-1)",
            fontVariantNumeric: "tabular-nums",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {formatValue(margenReal)}
          <Box
            component="span"
            sx={{
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "11.5px",
              fontWeight: 600,
              backgroundColor: "var(--bg-2)",
              color: "var(--ink-3)",
            }}
          >
            {marginPctOfSales.toFixed(1)}%
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
